import { useCallback, useState, type ReactNode } from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminUserCoachRow,
  type AdminUserDetail,
  type AdminUserPayment,
  type AdminUserSubscriptionResult,
} from "api/adminApi";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  InfoRow,
  Input,
  Modal,
  SectionHeader,
  Select,
  Table,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  Textarea,
} from "pages/admin/ui";
import {
  formatAmount,
  formatDate,
  formatDateTime,
  oauthIssuerLabel,
  pauseStateLabel,
  paymentStatusLabel,
  planLabel,
  progressStatusLabel,
  restoredExpirationDate,
  subscriptionStatusLabel,
  subscriptionTone,
  todayInputValue,
  vendorTypeLabel,
} from "./userFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 열려 있는 액션 팝업. null 이면 닫힌 상태.
type ActionKind =
  | "extend"
  | "pause"
  | "resume"
  | "terminate"
  | "nickname"
  | "coach";

/**
 * 회원관리 — 회원번호로 찾아 상세를 보고, 기간 연장·일시정지·재개·종료·닉네임·코치 변경을 처리한다.
 *
 * 환불과 플랜 변경은 여기서 실행하지 않는다. 결제 이력과 "이 건에서 하위 플랜으로 내릴 때 취소할 금액"만
 * 보여주고, 실제 취소는 토스 콘솔에서 한다 (취소 사유 문자열에 따라 웹훅 처리가 갈리는 구간이라 v1 범위 밖).
 */
export default function UserSection() {
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [coaches, setCoaches] = useState<AdminUserCoachRow[]>([]);

  const load = async (userId: number) => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await adminApi.userDetail(userId);
      if (res.code === 0 && res.result) {
        setDetail(res.result);
      } else {
        setDetail(null);
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setDetail(null);
      const status = (e as AxiosError).response?.status;
      setMessage(
        status === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      setLoading(false);
    }
  };

  const search = () => {
    const userId = Number(query.trim());
    if (!query.trim() || Number.isNaN(userId) || userId <= 0) {
      setMessage("회원번호를 숫자로 입력해 주세요");
      return;
    }
    load(userId);
  };

  // 입력이 바뀌면 이전 조회 결과를 즉시 비운다 — 낡은 상세를 보고 다른 회원에게 액션이 실행되는 것을 막는다.
  const onQueryChange = (value: string) => {
    setQuery(value);
    setDetail(null);
    setMessage(null);
  };

  // 코치 목록은 코치 변경 팝업을 처음 열 때 한 번만 받아 둔다.
  const openCoachModal = useCallback(async () => {
    setAction("coach");
    if (coaches.length > 0) return;
    try {
      const res = await adminApi.userCoachList();
      if (res.code === 0 && res.result) setCoaches(res.result);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    }
  }, [coaches.length]);

  // 구독 관련 처리는 결과를 그대로 돌려받아 로컬 패치한다 (재조회 왕복 없이 화면이 즉시 맞는다).
  const patchSubscription = (result: AdminUserSubscriptionResult) => {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            user: { ...prev.user, progressStatus: result.progressStatus },
            subscription: {
              ...prev.subscription,
              status: result.subscriptionStatus,
              subscriptionExpirationDate: result.subscriptionExpirationDate,
              bonusDays: result.bonusDays,
            },
            pause: {
              ...prev.pause,
              isPaused: !!result.pausedAt,
              pausedAt: result.pausedAt,
              resumedAt: result.resumedAt,
              pausedDays: result.pausedAt ? 0 : null,
            },
          }
        : prev
    );
  };

  // 처리 이력은 서버가 다시 계산해 주는 게 정확하다 — 액션 후 상세만 조용히 다시 받는다.
  const reload = () => {
    if (detail) load(detail.user.userId);
  };

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="회원관리">
        <Input
          className="w-56"
          fullWidth={false}
          inputMode="numeric"
          placeholder="회원번호 (userId)"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") search();
          }}
        />
        <Button
          variant="primary"
          onClick={search}
          loading={loading}
          loadingLabel="조회 중"
        >
          조회
        </Button>
      </SectionHeader>

      {message && <ErrorBanner>{message}</ErrorBanner>}

      {!detail && !loading && !message && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 py-20">
          <p className="text-sm font-medium text-slate-600">
            회원번호로 회원을 찾아 주세요
          </p>
          <p className="text-sm text-slate-400">
            상세를 확인한 뒤 기간 연장·일시정지·종료·닉네임·코치 변경을 처리할
            수 있어요.
          </p>
        </div>
      )}

      {loading && !detail && (
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5">
              <span className="block h-4 w-24 animate-pulse rounded bg-slate-100" />
              <div className="mt-4 flex flex-col gap-2.5">
                {[0, 1, 2, 3].map((j) => (
                  <span
                    key={j}
                    className="block h-4 w-full animate-pulse rounded bg-slate-100"
                  />
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {detail && (
        <>
          <ActionBar
            detail={detail}
            onSelect={(kind) =>
              kind === "coach" ? openCoachModal() : setAction(kind)
            }
          />
          <DetailPanel detail={detail} />
          <PaymentTable payments={detail.payments} />
          <ActionLogTable detail={detail} />
        </>
      )}

      {detail && action === "extend" && (
        <ExtendModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "pause" && (
        <PauseModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "resume" && (
        <ResumeModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "terminate" && (
        <TerminateModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "nickname" && (
        <NicknameModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "coach" && (
        <CoachModal
          detail={detail}
          coaches={coaches}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
    </section>
  );
}

// ------------------------------------------------------------------ 액션 바

function ActionBar({
  detail,
  onSelect,
}: {
  detail: AdminUserDetail;
  onSelect: (kind: ActionKind) => void;
}) {
  const { pause, user } = detail;
  const isLeft = user.progressStatus === "leave";

  return (
    <Card className="flex flex-wrap items-center gap-2 p-4">
      <Button
        variant="primary"
        onClick={() => onSelect("extend")}
        disabled={isLeft}
      >
        기간 연장
      </Button>
      {pause.isPaused ? (
        <Button onClick={() => onSelect("resume")}>일시정지 종료</Button>
      ) : (
        <Button onClick={() => onSelect("pause")} disabled={isLeft}>
          일시정지
        </Button>
      )}
      <Button onClick={() => onSelect("nickname")} disabled={isLeft}>
        닉네임 변경
      </Button>
      <Button onClick={() => onSelect("coach")} disabled={isLeft}>
        코치 변경
      </Button>
      <Button
        variant="danger"
        className="ml-auto"
        onClick={() => onSelect("terminate")}
        disabled={isLeft}
      >
        종료
      </Button>
      {isLeft && (
        <p className="w-full text-xs text-slate-500">
          탈퇴한 계정이라 처리할 수 있는 항목이 없어요.
        </p>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ 상세 패널

function DetailPanel({ detail }: { detail: AdminUserDetail }) {
  const { user, subscription, pause, coach, referral } = detail;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5">
        <PanelTitle>회원 정보</PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          <InfoRow label="회원번호">{user.userId}</InfoRow>
          <InfoRow label="닉네임">{user.nickname || "없음"}</InfoRow>
          <InfoRow label="이메일">{user.email || "없음"}</InfoRow>
          <InfoRow label="가입 방식">
            {oauthIssuerLabel(user.oauthIssuer)}
          </InfoRow>
          <InfoRow label="가입일">
            {formatDate(user.signedUpDate) || "없음"}
          </InfoRow>
          <InfoRow label="진행 상태">
            {progressStatusLabel(user.progressStatus)}
          </InfoRow>
          <InfoRow label="프로그램 시작">
            {formatDate(user.programStartedAt) || "시작 전"}
          </InfoRow>
          <InfoRow label="진행 일차">
            {user.programDays > 0
              ? `${user.programDays}일차 (${user.programWeeks}주차)`
              : "시작 전"}
          </InfoRow>
          {user.programStoppedAt && (
            <InfoRow label="중단 일시">
              {formatDateTime(user.programStoppedAt)}
            </InfoRow>
          )}
          {user.leavedAt && (
            <InfoRow label="탈퇴 일시">{formatDateTime(user.leavedAt)}</InfoRow>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <PanelTitle>
          이용 상태
          <Badge tone={subscriptionTone(pause, subscription)}>
            {pause.isPaused
              ? pauseStateLabel(pause)
              : subscriptionStatusLabel(subscription.status)}
          </Badge>
        </PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          <InfoRow label="플랜">{planLabel(user.plan)}</InfoRow>
          <InfoRow label="이용 시작">
            {formatDate(subscription.subscriptionStartedAt) || "없음"}
          </InfoRow>
          <InfoRow label="이용 기한">
            {subscription.subscriptionExpirationDate
              ? `${formatDate(subscription.subscriptionExpirationDate)} (${
                  subscription.expiryDaysLeft ?? 0
                }일 남음)`
              : "없음"}
          </InfoRow>
          <InfoRow label="누적 연장">{`${subscription.bonusDays ?? 0}일`}</InfoRow>
          {pause.isPaused && (
            <InfoRow label="정지 경과">
              {`${pause.pausedDays ?? 0}일 (${formatDate(pause.pausedAt)} 정지)`}
            </InfoRow>
          )}
          <InfoRow label="정지 쿼터">
            {`${pause.usedDays} / ${pause.totalDays}일 · ${pause.usedCount} / ${pause.totalCount}회`}
          </InfoRow>
          {pause.reservation && (
            <InfoRow label="회원 예약">
              {`${formatDate(pause.reservation.startDate)} ~ ${formatDate(
                pause.reservation.endDate
              )}`}
            </InfoRow>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <PanelTitle>담당 코치</PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          {coach ? (
            <>
              <InfoRow label="코치">
                {`${coach.coachName} (${coach.coachDisplayName})`}
              </InfoRow>
              <InfoRow label="재직 여부">
                {coach.isActive ? "재직" : "퇴사"}
              </InfoRow>
              <InfoRow label="배정 일시">
                {formatDateTime(coach.coachAssignedAt) || "없음"}
              </InfoRow>
              <InfoRow label="채팅방">
                {coach.hasChatChannel ? "열려 있음" : "없음"}
              </InfoRow>
            </>
          ) : (
            <p className="text-sm text-slate-400">
              아직 배정된 코치가 없어요. 프로그램을 시작하면 배정돼요.
            </p>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <PanelTitle>가입 경로</PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          {referral ? (
            <>
              <InfoRow label="레퍼럴 코드">{referral.code}</InfoRow>
              <InfoRow label="최종 유입">
                {formatDateTime(referral.lastTouchedAt) || "없음"}
              </InfoRow>
              <InfoRow label="기여 확정">
                {formatDateTime(referral.attributedAt) || "미확정"}
              </InfoRow>
            </>
          ) : (
            <p className="text-sm text-slate-400">
              레퍼럴 코드로 들어온 회원이 아니에요.
            </p>
          )}
          {user.promotion && (
            <InfoRow label="프로모션">{user.promotion}</InfoRow>
          )}
          {user.benefit && <InfoRow label="혜택">{user.benefit}</InfoRow>}
          {user.organization && (
            <InfoRow label="소속">{user.organization}</InfoRow>
          )}
          {user.isInsider && <InfoRow label="내부자">내부자 계정</InfoRow>}
        </div>
      </Card>
    </div>
  );
}

function PanelTitle({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ 결제 이력

function PaymentTable({ payments }: { payments: AdminUserPayment[] }) {
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader title="결제 이력" count={`${payments.length}건`} />
      <p className="text-xs text-slate-500">
        환불과 플랜 변경은 토스 콘솔에서 처리해요. 아래 취소 금액은 하위
        플랜으로 내릴 때 취소할 금액을 미리 계산한 값이에요.
      </p>
      <Table>
        <THead>
          <TH>주문번호</TH>
          <TH>상태</TH>
          <TH>수단</TH>
          <TH>결제일</TH>
          <TH numeric>결제 금액</TH>
          <TH>취소일</TH>
          <TH>다운그레이드 시 취소 금액</TH>
        </THead>
        <tbody>
          {payments.length === 0 && (
            <TEmpty colSpan={7}>결제 이력이 없는 회원이에요.</TEmpty>
          )}
          {payments.map((payment) => (
            <TRow key={payment.orderId}>
              <TD className="font-mono text-xs">{payment.orderId}</TD>
              <TD>
                <Badge tone={payment.status === "confirmed" ? "ok" : "idle"}>
                  {paymentStatusLabel(payment.status)}
                </Badge>
              </TD>
              <TD muted>{vendorTypeLabel(payment.vendorType)}</TD>
              <TD muted>{formatDate(payment.confirmedAt) || "미승인"}</TD>
              <TD numeric>{formatAmount(payment.payAmount)}</TD>
              <TD muted>{formatDate(payment.canceledAt)}</TD>
              <TD muted>
                {payment.planChangeHints.length === 0
                  ? ""
                  : payment.planChangeHints
                      .map(
                        (hint) =>
                          `${hint.targetPlanLabel} ${formatAmount(hint.cancelAmount)}`
                      )
                      .join(" · ")}
              </TD>
            </TRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ------------------------------------------------------------------ 처리 이력

function ActionLogTable({ detail }: { detail: AdminUserDetail }) {
  const logs = detail.actionLogs;
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader title="처리 이력" count={`최근 ${logs.length}건`} />
      <Table>
        <THead>
          <TH>일시</TH>
          <TH>담당자</TH>
          <TH>작업</TH>
          <TH>변경 내용</TH>
          <TH>메모</TH>
        </THead>
        <tbody>
          {logs.length === 0 && (
            <TEmpty colSpan={5}>아직 어드민이 처리한 기록이 없어요.</TEmpty>
          )}
          {logs.map((logRow, index) => (
            <TRow key={`${logRow.actionAt}-${index}`}>
              <TD muted>{formatDateTime(logRow.actionAt)}</TD>
              <TD muted>
                {logRow.adminEmail.replace("@orwellhealth.org", "")}
              </TD>
              <TD>{logRow.action}</TD>
              <TD muted className="whitespace-normal">
                {logRow.beforeSummary && logRow.afterSummary
                  ? `${logRow.beforeSummary} → ${logRow.afterSummary}`
                  : logRow.afterSummary || ""}
              </TD>
              <TD muted className="whitespace-normal">
                {logRow.memo || ""}
              </TD>
            </TRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ------------------------------------------------------------------ 액션 팝업

// 팝업마다 반복되는 상태(제출 중·오류·메모)와 제출 처리를 한군데로 모은다.
function useAction<T>(onDone: (result: T) => void) {
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (
    call: () => Promise<{ code: number; message?: string; result?: T }>
  ) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await call();
      if (res.code === 0 && res.result !== undefined) {
        onDone(res.result as T);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return { memo, setMemo, submitting, error, submit };
}

function MemoField({
  memo,
  setMemo,
}: {
  memo: string;
  setMemo: (value: string) => void;
}) {
  return (
    <Field label="메모" helper="처리 이력에 함께 남아요. 비워 두어도 괜찮아요.">
      <Textarea
        rows={2}
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        placeholder="예: 코치 변경 안내에 따른 보상"
      />
    </Field>
  );
}

function subjectOf(detail: AdminUserDetail) {
  return `${detail.user.userId} · ${detail.user.nickname || "닉네임 없음"}`;
}

type ModalProps = {
  detail: AdminUserDetail;
  onClose: () => void;
  onDone: (result: AdminUserSubscriptionResult) => void;
};

function ExtendModal({ detail, onClose, onDone }: ModalProps) {
  const [mode, setMode] = useState<"days" | "until">("days");
  const [days, setDays] = useState("14");
  const [untilDate, setUntilDate] = useState(
    detail.subscription.subscriptionExpirationDate || todayInputValue()
  );
  const [fromToday, setFromToday] = useState(false);
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const run = () =>
    submit(() =>
      adminApi.userExtend(detail.user.userId, {
        ...(mode === "days"
          ? { days: Number(days), fromToday }
          : { untilDate }),
        memo,
      })
    );

  return (
    <Modal
      title="기간 연장"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={mode === "days" && (!days || Number(days) <= 0)}
          >
            연장
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="현재 이용 기한">
          {formatDate(detail.subscription.subscriptionExpirationDate) || "없음"}
        </InfoRow>

        <Field label="연장 방식">
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as "days" | "until")}
          >
            <option value="days">일수로 연장</option>
            <option value="until">날짜를 지정해서 연장</option>
          </Select>
        </Field>

        {mode === "days" ? (
          <>
            <Field label="연장 일수" helper="현재 이용 기한에서 이만큼 더해요.">
              <Input
                inputMode="numeric"
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={fromToday}
                onChange={(e) => setFromToday(e.target.checked)}
              />
              현재 이용 기한 대신 오늘부터 계산하기
            </label>
          </>
        ) : (
          <Field label="이 날짜까지" helper="지정한 날짜로 이용 기한을 맞춰요.">
            <Input
              type="date"
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
            />
          </Field>
        )}

        {detail.pause.isPaused && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            일시정지 중인 회원이에요. 이용 기한만 늘어나고 정지는 그대로
            유지돼요.
          </p>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function PauseModal({ detail, onClose, onDone }: ModalProps) {
  const [indefinite, setIndefinite] = useState(true);
  const [endDate, setEndDate] = useState(todayInputValue());
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const run = () =>
    submit(() =>
      adminApi.userPause(detail.user.userId, indefinite ? null : endDate, memo)
    );

  return (
    <Modal
      title="일시정지"
      subject={subjectOf(detail)}
      description="오늘부터 바로 정지돼요. 이용 기한은 그대로 두고, 재개할 때 정지한 일수만큼 되돌려 줘요."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
          >
            정지
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        <Field label="정지 방식">
          <Select
            value={indefinite ? "indefinite" : "until"}
            onChange={(e) => setIndefinite(e.target.value === "indefinite")}
          >
            <option value="indefinite">무기한 정지</option>
            <option value="until">종료일 지정</option>
          </Select>
        </Field>

        {indefinite ? (
          <p className="text-sm leading-relaxed text-slate-500">
            자동으로 재개되지 않아요. 재개할 때 어드민이 직접 처리해야 해요.
          </p>
        ) : (
          <Field
            label="정지 종료일"
            helper="다음 날 아침에 자동으로 재개되고, 정지한 일수만큼 이용 기한이 늘어나요."
          >
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Field>
        )}

        {detail.pause.usedCount >= detail.pause.totalCount && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            회원이 쓸 수 있는 정지 횟수를 이미 다 썼어요. 어드민 정지는 그래도
            진행되지만, 회원의 남은 쿼터를 넘겨 쓰게 돼요.
          </p>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function ResumeModal({ detail, onClose, onDone }: ModalProps) {
  const [mode, setMode] = useState<"auto" | "until">("auto");
  const [extraDays, setExtraDays] = useState("0");
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const extra = Number(extraDays) || 0;
  const restored = restoredExpirationDate(
    detail.subscription,
    detail.pause,
    extra
  );
  const [untilDate, setUntilDate] = useState(restored || todayInputValue());

  const run = () =>
    submit(() =>
      adminApi.userResume(detail.user.userId, {
        ...(mode === "auto"
          ? extra > 0
            ? { extraDays: extra }
            : {}
          : { untilDate }),
        memo,
      })
    );

  return (
    <Modal
      title="일시정지 종료"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
          >
            재개
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="정지 경과">{`${detail.pause.pausedDays ?? 0}일`}</InfoRow>
        <InfoRow label="현재 이용 기한">
          {formatDate(detail.subscription.subscriptionExpirationDate) || "없음"}
        </InfoRow>

        <Field label="이용 기한 처리">
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as "auto" | "until")}
          >
            <option value="auto">정지한 일수만큼 자동 복원</option>
            <option value="until">이용 기한 직접 지정</option>
          </Select>
        </Field>

        {mode === "auto" ? (
          <>
            <Field
              label="추가 연장 일수"
              helper="복원한 기한에서 더 주고 싶은 일수만 적어요. 없으면 0."
            >
              <Input
                inputMode="numeric"
                value={extraDays}
                onChange={(e) => setExtraDays(e.target.value)}
              />
            </Field>
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {restored
                ? `처리 후 이용 기한은 ${formatDate(restored)} 이 돼요.`
                : "이용 기한이 없어 자동 복원을 계산할 수 없어요. 직접 지정해 주세요."}
            </p>
            <p className="text-sm leading-relaxed text-slate-500">
              이 도구가 생기기 전에 정지된 회원은 이용 기한이 이미 조정돼 있어서
              자동 복원이 맞지 않아요. 그런 회원은 서버가 처리를 막으니 직접
              지정으로 바꿔 주세요.
            </p>
          </>
        ) : (
          <Field
            label="이용 기한"
            helper="예전 방식으로 정지된 회원이라 자동 계산이 맞지 않을 때 써요."
          >
            <Input
              type="date"
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
            />
          </Field>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function TerminateModal({ detail, onClose, onDone }: ModalProps) {
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const run = () => {
    if (
      !window.confirm(
        `${subjectOf(detail)} 회원의 프로그램을 오늘부로 종료할까요? 되돌리려면 기간 연장으로 다시 살려야 해요.`
      )
    ) {
      return;
    }
    submit(() => adminApi.userTerminate(detail.user.userId, memo));
  };

  return (
    <Modal
      title="조기종결"
      subject={subjectOf(detail)}
      description="프로그램을 종료하고 이용 기한을 오늘로 당겨요. 환불은 함께 처리되지 않고, 회원에게 푸시도 보내지 않아요."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="danger"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
          >
            종료
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function NicknameModal({
  detail,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [nickname, setNickname] = useState(detail.user.rawNickname || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memo, setMemo] = useState("");

  const run = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminApi.userNickname(
        detail.user.userId,
        nickname.trim(),
        memo
      );
      if (res.code === 0 && res.result) {
        if (!res.result.chatSynced && detail.coach?.hasChatChannel) {
          window.alert(
            "닉네임은 바뀌었지만 채팅방 이름 동기화에 실패했어요. 채팅방 표시를 확인해 주세요."
          );
        }
        onDone();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const trimmed = nickname.trim();
  const invalid = trimmed.length === 0 || trimmed.length > 12;

  return (
    <Modal
      title="닉네임 변경"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={invalid || trimmed === detail.user.rawNickname}
          >
            변경
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="현재 닉네임">{detail.user.nickname || "없음"}</InfoRow>
        <Field
          label="새 닉네임"
          helper="12자까지 쓸 수 있어요. 같은 닉네임이 있으면 순번이 자동으로 붙어요."
          error={trimmed.length > 12 ? "12자 이하로 입력해 주세요" : null}
        >
          <Input
            value={nickname}
            invalid={trimmed.length > 12}
            onChange={(e) => setNickname(e.target.value)}
          />
        </Field>
        <p className="text-sm leading-relaxed text-slate-500">
          채팅방에 보이는 이름도 함께 바뀌어요.
        </p>
        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function CoachModal({
  detail,
  coaches,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  coaches: AdminUserCoachRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [coachId, setCoachId] = useState("");
  const [sendAnnouncement, setSendAnnouncement] = useState(true);
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminApi.userCoachChange(
        detail.user.userId,
        Number(coachId),
        sendAnnouncement,
        memo
      );
      if (res.code === 0 && res.result) {
        if (res.result.warning) window.alert(res.result.warning);
        if (sendAnnouncement && !res.result.announcementSent) {
          window.alert(
            "코치는 변경했지만 안내 메시지는 나가지 않았어요. 오늘 이미 안내가 나갔거나 채팅방이 없는 회원이에요."
          );
        }
        onDone();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const selectable = coaches.filter(
    (coach) => coach.coachId !== detail.coach?.coachId
  );

  return (
    <Modal
      title="담당 코치 변경"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={!coachId}
          >
            변경
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="현재 코치">
          {detail.coach ? detail.coach.coachName : "없음"}
        </InfoRow>
        <Field
          label="새 담당 코치"
          helper="괄호 안은 지금 담당 중인 진행 내담자 수예요."
        >
          <Select value={coachId} onChange={(e) => setCoachId(e.target.value)}>
            <option value="">코치를 골라 주세요</option>
            {selectable.map((coach) => (
              <option key={coach.coachId} value={coach.coachId}>
                {`${coach.name} (${coach.assignedPatientCount}명)`}
                {coach.isMasterPlanExcluded ? " · 마스터 제외" : ""}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={sendAnnouncement}
            onChange={(e) => setSendAnnouncement(e.target.checked)}
          />
          채팅방에 코치 변경 안내를 보내기
        </label>
        {!detail.coach?.hasChatChannel && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            채팅방이 없는 회원이라 채팅 멤버 교체와 안내 발송이 일어나지 않아요.
          </p>
        )}
        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}
